/**
 * Environment-agnostic encoding helpers (work in Node and the browser).
 * No dependency on Buffer / atob / btoa so the exact same code runs everywhere.
 */

const B64URL_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

const B64URL_LOOKUP: Int16Array = (() => {
  const t = new Int16Array(128).fill(-1);
  for (let i = 0; i < B64URL_ALPHABET.length; i++) {
    t[B64URL_ALPHABET.charCodeAt(i)] = i;
  }
  // Accept standard base64 chars too, for leniency.
  t["+".charCodeAt(0)] = 62;
  t["/".charCodeAt(0)] = 63;
  return t;
})();

export function bytesToBase64Url(bytes: Uint8Array): string {
  let out = "";
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8) | bytes[i + 2]!;
    out +=
      B64URL_ALPHABET[(n >> 18) & 63]! +
      B64URL_ALPHABET[(n >> 12) & 63]! +
      B64URL_ALPHABET[(n >> 6) & 63]! +
      B64URL_ALPHABET[n & 63]!;
  }
  const rem = bytes.length - i;
  if (rem === 1) {
    const n = bytes[i]! << 16;
    out += B64URL_ALPHABET[(n >> 18) & 63]! + B64URL_ALPHABET[(n >> 12) & 63]!;
  } else if (rem === 2) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8);
    out +=
      B64URL_ALPHABET[(n >> 18) & 63]! +
      B64URL_ALPHABET[(n >> 12) & 63]! +
      B64URL_ALPHABET[(n >> 6) & 63]!;
  }
  return out;
}

export function base64UrlToBytes(input: string): Uint8Array {
  const s = input.replace(/=+$/, "");
  const len = s.length;
  const outLen = Math.floor((len * 3) / 4);
  const out = new Uint8Array(outLen);
  let bits = 0;
  let acc = 0;
  let o = 0;
  for (let i = 0; i < len; i++) {
    const c = s.charCodeAt(i);
    const v = c < 128 ? B64URL_LOOKUP[c]! : -1;
    if (v < 0) throw new Error("invalid base64url character");
    acc = (acc << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[o++] = (acc >> bits) & 0xff;
    }
  }
  return out.subarray(0, o);
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function utf8ToBytes(s: string): Uint8Array {
  return encoder.encode(s);
}

export function bytesToUtf8(b: Uint8Array): string {
  return decoder.decode(b);
}

/** Lower-case hex encoding. */
export function bytesToHex(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i]!.toString(16).padStart(2, "0");
  }
  return out;
}

export function concatBytes(...parts: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

/** Encode the shareable pairing token: `zc1.<channelId>.<secretB64url>`.
 *  Used as the URL-fragment payload of a pairing link (carries the link factor
 *  only — the 6-digit code is shared separately). */
export function encodePairingToken(
  channelId: string,
  secretB64url: string
): string {
  return `zc1.${channelId}.${secretB64url}`;
}

/** Encode the QR pairing token that also bundles the out-of-band code:
 *  `zc1.<channelId>.<secretB64url>.<code>[.<deviceFingerprint>]`. An in-person
 *  QR scan then needs no separate code entry. The optional device fingerprint
 *  (§5.1) lets the joiner confirm which device created the channel. */
export function encodeQrPairingToken(
  channelId: string,
  secretB64url: string,
  code: string,
  deviceFingerprint?: string
): string {
  const base = `zc1.${channelId}.${secretB64url}.${code}`;
  return deviceFingerprint ? `${base}.${deviceFingerprint}` : base;
}

/** Decode a pairing token, a QR token, or a full pairing link (the `zc1.…`
 *  fragment after `#`). Returns the optional bundled `code` and
 *  `deviceFingerprint` when present. */
export function decodePairingToken(token: string): {
  channelId: string;
  secret: string;
  code?: string;
  deviceFingerprint?: string;
} | null {
  let trimmed = token.trim();
  const hash = trimmed.lastIndexOf("#");
  if (hash >= 0) trimmed = trimmed.slice(hash + 1);
  // Tolerate a full invite link like `…/#/join/zc1.<…>` by locating the token.
  const zc = trimmed.lastIndexOf("zc1.");
  if (zc >= 0) trimmed = trimmed.slice(zc);
  const parts = trimmed.split(".");
  if (parts[0] !== "zc1") return null;
  if (parts.length === 3) {
    const channelId = parts[1]!;
    const secret = parts[2]!;
    if (!channelId || !secret) return null;
    return { channelId, secret };
  }
  if (parts.length === 4) {
    const channelId = parts[1]!;
    const secret = parts[2]!;
    const code = parts[3]!;
    if (!channelId || !secret || !code) return null;
    return { channelId, secret, code };
  }
  if (parts.length === 5) {
    const channelId = parts[1]!;
    const secret = parts[2]!;
    const code = parts[3]!;
    const deviceFingerprint = parts[4]!;
    if (!channelId || !secret || !code || !deviceFingerprint) return null;
    return { channelId, secret, code, deviceFingerprint };
  }
  return null;
}
